import { Stack } from '@mui/material';

import Note from '@/designs-dev/annotations/Note';
import Looping from '@/designs-dev/handover/Looping';
import { type HandoverSpec } from '@/designs-dev/handover/types';
import LockScreen, { PushNotification } from '@/designs-dev/device/LockScreen';
import Flow from '@/karehero-app/tell-us-once/intake/Flow';
import {
  CARER_INITIALS,
  INTAKE_PING,
} from '@/karehero-app/tell-us-once/intake/content';
import CareProfileScreen from '@/karehero-app/tell-us-once/intake/CareProfileScreen';

const at = (initial: Parameters<typeof Flow>[0]['initial']) => () => (
  <Flow initial={initial} option={2} />
);

// The same frame, playing its own transition over and over. The chat hands its
// replay handle up and `Looping` presses it, so the movement is the prototype's
// rather than a description of it.
const moving = (initial: Parameters<typeof Flow>[0]['initial']) => () => (
  <Looping
    render={(onReplay) => (
      <Flow initial={initial} option={2} onReplay={onReplay} />
    )}
  />
);

// The pieces the intake is built from, named, with a line each on when to reach
// for one.
//
// A page of its own because the choice is the design. Nearly every question in
// this flow is one of these, and picking wrong is what makes an intake feel like
// a form: a hard gate where the answer needed words, an open orb where the
// answer had to be exact.
//
// Grouped by the spine every intake follows. How we ask. How they answer. How we
// show what we read. Where it lands.
export const patternsSpec: HandoverSpec = {
  id: 'patterns',
  title: 'Patterns 3.0',
  description:
    'The pieces the intake is built from, and when to reach for each one. What moves as a question changes is under Interaction design, playing on a loop, so the movement can be looked at without clicking through to it.',
  links: [
    {
      label: 'Play the bar transition',
      url: '/tell-us-once/intake-profile-review?scene=chat-v2',
    },
    {
      label: 'Play the orb arriving',
      url: '/tell-us-once/intake-profile-review?scene=chat-v2-who',
    },
  ],
  readme: (
    <Note title='Readme' variant='body2'>
      <Stack spacing={1.5}>
        <span>
          One pattern per kind of data we need. Most of them can be reused
          wherever the same kind of answer comes up.
        </span>
        <span>
          The chat interface changes a bit between them, and that is a technical
          constraint rather than a preference. This is not a synchronous
          conversation: nothing is read while they talk. We want it to feel as
          close to one as we can, which means not accepting an answer we are not
          ready to process, so a question we cannot read a free answer to takes
          the input away rather than swallowing something we would drop.
        </span>
        <span>
          Once it is synchronous the shape stops changing. The chat can stay
          open on a closed question, and the model handles the rest: asking them
          to pick one of the answers, or taking a typed or spoken answer that
          matches one. We do not have that yet.
        </span>
        <span>
          So this is a phase one stopgap. A bar that appears and disappears is
          not best practice and we know it.
        </span>
        <span>
          The app bar above all of this is not one of these patterns, but it is
          not optional either. Every screen from account creation onwards
          carries it, the intake included, because the avatar is the only route
          to Help and Sign out until Home exists. It shows a person icon until
          the second question has their name, and their initials after it. The
          rule sits on the Sign-up &amp; onboarding handover, on the Welcome
          &amp; funding step, where the bar first appears.
        </span>
      </Stack>
    </Note>
  ),
  flows: [],
  patterns: [
    {
      id: 'typed-only',
      name: 'Typed only',
      when: 'An answer that has to be exact. No orb, so there is nothing to talk into, and a misheard name is a rejected claim.',
      group: 'ask',
      render: at('chat-v2-who'),
    },
    {
      id: 'answers-as-rows',
      name: 'Answers as rows',
      when: 'A closed question. Tapping one answers it. A hard gate, so the way to send is taken away, and it animates out rather than vanishing.',
      group: 'ask',
      render: at('chat-v2-fork'),
    },
    {
      id: 'open-capture',
      name: 'Talk or type',
      when: 'Something that will not fit a field. The orb sits on the bar, typing stays available, and a second bubble says what to tap.',
      group: 'ask',
      render: at('chat-v2-situation'),
    },
    {
      id: 'way-on',
      name: "Continue when you're done",
      when: 'A question they can add to as many times as they like. One row leaves it, and it is the only lime one on the screen.',
      group: 'ask',
      note: (
        <Note colour='sunflower' variant='body2'>
          <Stack spacing={1.5}>
            <span>
              Again, the asynchronicity. This question lets them add as much as
              they want and we understand none of it as it arrives, so something
              has to say when they are finished. This is that.
            </span>
            <span>
              It stays at the bottom. Anything they send lands above it, so it
              is always the last thing in the conversation, and pressing it is
              what moves on.
            </span>
          </Stack>
        </Note>
      ),
      render: at('chat-v2-stuck'),
    },
    {
      id: 'prompt-and-open',
      name: 'A prompt, chat still open',
      when: 'Not a gate. The likely answer is one tap, and typing something else works exactly as it did.',
      group: 'answer',
      render: at('chat-v2-preferred-name'),
    },
    {
      id: 'the-plus',
      name: 'The plus',
      when: 'Handing something over instead of saying it. The plus asks which, because the flow takes both.',
      group: 'answer',
      render: at('chat-v2-attach'),
    },
    {
      id: 'an-upload',
      name: 'An upload as the answer',
      when: 'A photo or a file answering the question, sat in the transcript where they sent it.',
      group: 'answer',
      render: at('chat-v2-who-photo'),
    },
    {
      id: 'recording',
      name: 'Recording',
      when: 'Mid recording. A mic where the play button goes, a counter running, and the way to stop where send would be.',
      group: 'answer',
      render: at('chat-v2-recording'),
    },
    {
      id: 'heard-back',
      name: 'Heard back before it sends',
      when: 'Stopped, not sent. Sending is a decision rather than a side effect of stopping, so there is a bin and a send.',
      group: 'answer',
      render: at('chat-v2-voice'),
    },
    {
      id: 'the-beat',
      name: 'A beat before it replies',
      when: 'Every answer that gets read. The dots land in the bubble the reply is coming in, so the message arrives in a shape that was already there.',
      group: 'read',
      note: (
        <Note colour='sunflower' variant='body2'>
          <Stack spacing={1.5}>
            <span>
              900ms, held open here so it can be looked at. Long enough to read
              as somebody reading, short enough that nobody waits for it.
            </span>
            <span>
              Nothing is actually being read in that beat, and every question
              gets the same one. It is there because a question that arrives in
              the same frame as the answer to the last one reads as a form
              validating rather than somebody replying.
            </span>
          </Stack>
        </Note>
      ),
      render: at('chat-v2-reading'),
    },
    {
      id: 'read-back',
      name: 'Read back as sentences',
      when: 'What we think we heard, in prose and marked as ours. Asks whether we understood, which a table of fields cannot.',
      group: 'read',
      render: at('summary'),
    },
    {
      id: 'facts-with-provenance',
      name: 'What we read, and where from',
      when: 'The same facts as a list, for changing one thing. A sparkle on anything nobody has checked.',
      group: 'read',
      render: at('review-facts'),
    },
    {
      id: 'edit-in-a-drawer',
      name: 'Change one answer in a drawer',
      when: 'Any list of answers: the review, the profile, the calculator’s answers we already have. The pencil opens that row on its own in a drawer, with the control its answer takes. Never a text field in the list.',
      group: 'read',
      render: () => (
        <CareProfileScreen
          initialTab='profile'
          editing='address'
          account={{ initials: CARER_INITIALS }}
        />
      ),
    },
    {
      id: 'the-ping',
      name: 'A ping brings them back',
      when: 'Reading happens after they have gone, so the way back in has to come and find them.',
      group: 'keep',
      render: () => (
        <LockScreen>
          <PushNotification {...INTAKE_PING} />
        </LockScreen>
      ),
    },
    {
      id: 'nothing-waits',
      name: 'Nothing waits on them',
      when: 'A pause with no action on it. Being pinged is the way out, so a button would either lie or invite them to wait.',
      group: 'keep',
      render: at('processing'),
    },
    {
      id: 'centred-minus-the-bar',
      name: 'Centred, minus the bar',
      when: 'A screen that is one block: a wait, an interstitial, an empty state. It centres against the frame rather than against the space under the bar.',
      group: 'layout',
      note: (
        <Note title='The arithmetic' colour='sunflower' variant='body2'>
          <Stack spacing={1.5}>
            <span>
              A box that fills what is left centres its content between what is
              above it and what is below. A status bar and a home indicator are
              the same height, so most screens are already centred and need
              nothing.
            </span>
            <span>
              A bar is not answered by anything at the bottom, so the box gives
              that height back as bottom padding. That lifts the content by half
              of it, which is exactly the gap, and puts it on the frame's centre
              line. Where a tab bar answers the bar, there is nothing to give
              back.
            </span>
          </Stack>
        </Note>
      ),
      render: at('processing'),
    },
  ],
  interactions: [
    {
      id: 'bar-arrives',
      name: 'The bar comes up',
      what: 'A question answered in the conversation has no bar at all. The one after it needs an input, so the bar comes up rather than being there.',
      render: moving('chat-v2-fork'),
    },
    {
      id: 'orb-arrives',
      name: 'The orb, a beat behind',
      what: 'A typed question handing over to one they can talk to. The bar that is going drops away, the next one comes up, and the orb follows it.',
      note: (
        <Note title='Timings' colour='sunflower' variant='body2'>
          <Stack spacing={1.5}>
            <span>
              The bar drops down and comes back up over 220ms. The orb waits
              90ms after that and takes 240ms of its own, so it is the thing you
              notice rather than part of the furniture riding up with the bar.
            </span>
            <span>
              Only a change of kind moves. Two typed questions in a row are the
              same bar with a new placeholder, and sliding that out and back in
              would say something changed when nothing did.
            </span>
          </Stack>
        </Note>
      ),
      render: moving('chat-v2-who'),
    },
  ],
  states: [
    {
      id: 'fail-send',
      title: 'It did not send',
      kind: 'error',
      repro:
        'The message carries the failure and offers the way out. No alert: the thing that failed is the thing that says so',
      render: at('fail-send'),
    },
    {
      id: 'fail-upload-photo',
      title: 'It did not upload',
      kind: 'error',
      repro:
        'Same treatment around an attachment, which draws nothing like a bubble',
      render: at('fail-upload-photo'),
    },
    {
      id: 'fail-one-name',
      title: 'A validation error, in the chat',
      kind: 'error',
      repro:
        'The same thing a form would tell you, said rather than shown. It says what it has, what is missing and why we need it, and the question stays open',
      render: at('fail-one-name'),
    },
    {
      id: 'summary-problem',
      title: 'Told on a screen, not in the chat',
      kind: 'error',
      repro:
        'A failure they were not present for. An alert, what we did get, and the choice to carry on or say it again',
      render: at('summary-problem'),
    },
  ],
  components: {},
};
